// src/modals/ConfirmModal.jsx
// Phase 37 Plan 06 — extracted from src/App.jsx (was lines 390-461 pre-Plan-37-06).
//
// Scope: Replaces browser confirm dialogs with a styled, accessible dialog.
// Phase 31.1 Plan 02 added optional typedConfirmString (case-sensitive
// typed-string gate for destructive actions, #208 D-05) and variant
// ('danger' | 'neutral') for non-destructive replace-style confirms (#208 D-06).
// Backward-compatible: both new props have defaults so existing call sites
// (AreaManager, removePlant, etc.) keep their original behaviour with no change.

import { useState, useRef, useEffect } from 'react';

export function ConfirmModal({title, message, confirmLabel, onConfirm, onCancel,
                              typedConfirmString = null, variant = 'danger'}) {
  const confirmRef = useRef(null);
  const cancelRef = useRef(null);
  const inputRef = useRef(null);
  const triggerRef = useRef(document.activeElement);
  const [inputValue, setInputValue] = useState('');

  const needsTyping = typeof typedConfirmString === 'string' && typedConfirmString.length > 0;
  // STRICT equality — no trim, no case-fold. Per RESEARCH Pitfall 2.
  const canConfirm = !needsTyping || inputValue === typedConfirmString;

  useEffect(()=>{
    // Initial focus — input when typing-gated, confirm otherwise.
    (needsTyping ? inputRef : confirmRef).current?.focus();
    const handleKey = e => {
      if(e.key==="Escape") { e.preventDefault(); onCancel(); }
      if(e.key==="Tab") {
        e.preventDefault();
        if(e.shiftKey) {
          if(document.activeElement===cancelRef.current) confirmRef.current?.focus();
          else cancelRef.current?.focus();
        } else {
          if(document.activeElement===confirmRef.current) cancelRef.current?.focus();
          else confirmRef.current?.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKey);
    return ()=>{ document.removeEventListener("keydown", handleKey); triggerRef.current?.focus(); };
  },[onCancel, needsTyping]);

  const confirmBg = variant === 'neutral' ? 'var(--c-heading)' : 'var(--c-danger)';
  const confirmColor = variant === 'neutral' ? '#fff' : 'var(--c-danger-text)';

  return (
    <div
      onClick={onCancel}
      style={{position:"fixed",inset:0,background:"var(--c-overlay)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
    >
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--c-bg)",borderRadius:18,padding:28,maxWidth:420,width:"100%",boxShadow:"0 8px 40px rgba(0,0,0,0.2)"}}>
        <h2 id="confirm-modal-title" style={{fontFamily:"var(--font-family-display)",fontSize:"var(--font-h3)",color:"var(--c-heading)",margin:"0 0 12px",fontWeight:700}}>{title}</h2>
        <p style={{fontSize:"var(--font-body)",color:"var(--c-text)",margin:"0 0 20px",lineHeight:1.5}}>{message}</p>
        {needsTyping&&(
          <div style={{marginBottom:16}}>
            <label htmlFor="confirm-modal-input"
              className="label-block">
              Type {typedConfirmString} to confirm
            </label>
            <input id="confirm-modal-input" ref={inputRef} type="text"
              value={inputValue}
              onChange={e=>setInputValue(e.target.value)}
              autoComplete="off" spellCheck={false}
              style={{padding:"8px 12px",borderRadius:8,border:"1px solid var(--c-border)",fontSize:'var(--font-input)',width:"100%",boxSizing:"border-box",background:"var(--c-bg)",color:"var(--c-text)"}} // UAT2-GAP-07 iOS zoom floor
              aria-describedby="confirm-modal-title"/>
          </div>
        )}
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button ref={cancelRef} className="touch-44" onClick={onCancel} style={{background:"var(--c-btn-bg)",color:"var(--c-btn-text)",border:"none",borderRadius:20,padding:"9px 22px",fontSize:'var(--font-body)',fontWeight:600,cursor:"pointer"}} aria-label="Cancel">Cancel</button>
          <button ref={confirmRef}
            className="touch-44"
            onClick={canConfirm?onConfirm:undefined}
            disabled={!canConfirm}
            style={{background:confirmBg,color:confirmColor,border:"none",borderRadius:20,padding:"9px 22px",fontSize:'var(--font-body)',fontWeight:600,cursor:canConfirm?"pointer":"not-allowed",opacity:canConfirm?1:0.5}}
            aria-label={confirmLabel}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
